import { Binary, ChevronLeft, ShieldAlert, ScanIcon, X, XCircle } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useQrScanner, { QR_ELEMENT_ID } from '../../hooks/useQrScanner';
import { darkTheme, lightTheme } from '../../theme/theme';
import { PRODUCTS, detectProductFromPath, setActiveProduct } from '../../utils/urlConfig';
import { parseGS1 } from '../../utils/gs1Parser';
import useTheme from '../../hooks/useTheme';

// ─────────────────────────────────────────────────────────────────────────────
// Sub-components (kept in this file – they're tightly coupled to screen state)
// ─────────────────────────────────────────────────────────────────────────────

/** Animated scan-line overlay rendered on top of the camera feed. */
const ScanOverlay = ({ buttonColor }) => (
  <div
    className="absolute inset-0 z-10 flex flex-col items-center justify-center"
    style={{ pointerEvents: 'none' }}
  >
    <div style={{ position: 'relative', width: 250, height: 250, marginBottom: 20 }}>
      {/* Corner brackets */}
      {[
        { top: 0,    left: 0,     borderTop: true,    borderLeft: true,   borderRadius: '8px 0 0 0' },
        { top: 0,    right: 0,    borderTop: true,    borderRight: true,  borderRadius: '0 8px 0 0' },
        { bottom: 0, left: 0,     borderBottom: true, borderLeft: true,   borderRadius: '0 0 0 8px' },
        { bottom: 0, right: 0,    borderBottom: true, borderRight: true,  borderRadius: '0 0 8px 0' },
      ].map((corner, i) => (
        <div
          key={i}
          style={{
            position: 'absolute',
            width: 40,
            height: 40,
            ...corner,
            borderTopWidth:    corner.borderTop    ? 4 : 0,
            borderBottomWidth: corner.borderBottom ? 4 : 0,
            borderLeftWidth:   corner.borderLeft   ? 4 : 0,
            borderRightWidth:  corner.borderRight  ? 4 : 0,
            borderStyle: 'solid',
            borderColor: buttonColor,
          }}
        />
      ))}

      {/* Scanning laser line */}
      <div
        style={{
          position: 'absolute',
          top: 0, left: 0, right: 0,
          height: 2,
          backgroundColor: buttonColor,
          boxShadow: `0 0 10px ${buttonColor}`,
          animation: 'scanLine 2.5s ease-in-out infinite',
        }}
      />
    </div>

    <p style={{ color: '#fff', fontSize: 14, opacity: 0.85, fontWeight: 500, letterSpacing: 0.5 }}>
      Align QR code within the frame
    </p>
  </div>
);

/** Display panel shown when not scanning: device QR code + action buttons. */
const DisplayPanel = ({ theme, qrValue, scanError, onScanTrigger, onOpenSerialPopup }) => (
  <div className="absolute inset-0 w-full h-full flex flex-col items-center justify-center z-0 gap-6">
    {/* Scan QR Code Indicator */}
    <button
      type="button"
      onClick={onScanTrigger}
      className="relative active:scale-[0.98] transition-all duration-200 cursor-pointer group"
    >
      {/* Main Container with subtle hover effect */}
      <div
        className="relative rounded-2xl flex flex-col items-center justify-center p-12 transition-all duration-300"
        style={{ 
          backgroundColor: theme.background2, 
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
          border: `1px solid ${theme.stroke}`,
          width: '340px',
        }}
      >
        {/* Subtle gradient overlay on hover */}
        <div 
          className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
          style={{
            background: `linear-gradient(135deg, ${theme.button}08, transparent)`,
          }}
        />
        
        {/* Icon container */}
        <div className="relative mb-5" style={{ animation: 'scale-pulse 3s ease-in-out infinite' }}>
          <div
            className="w-24 h-24 rounded-2xl flex items-center justify-center transition-all duration-300 group-hover:scale-110"
            style={{ 
              backgroundColor: `${theme.button}15`,
            }}
          >
            <ScanIcon size={44} color={theme.button} strokeWidth={2} />
          </div>
          
          {/* Small pulse indicator */}
          <div
            className="absolute -top-1 -right-1 w-3 h-3 rounded-full"
            style={{
              backgroundColor: theme.button,
              animation: 'pulse-dot 2s ease-in-out infinite',
              boxShadow: `0 0 0 0 ${theme.button}`,
            }}
          />
        </div>
        
        {/* Text content */}
        <h3 
          className="text-base font-semibold mb-1"
          style={{ color: theme.text }}
        >
          Scan QR Code
        </h3>
        <p 
          className="text-xs text-center"
          style={{ color: theme.subText }}
        >
          Tap to activate camera
        </p>
      </div>
    </button>

    {scanError && (
      <p className="text-sm text-center px-8" style={{ color: '#f87171' }}>
        {scanError}
      </p>
    )}

    {/* OR Divider */}
    <div className="flex items-center gap-4 w-full max-w-xs px-6">
      <div className="flex-1 h-px" style={{ backgroundColor: theme.stroke }} />
      <span className="text-xs font-semibold" style={{ color: theme.subText }}>OR</span>
      <div className="flex-1 h-px" style={{ backgroundColor: theme.stroke }} />
    </div>

    <button
      type="button"
      onClick={onOpenSerialPopup}
      className="flex items-center gap-2 py-3 px-10 rounded-full border backdrop-blur-sm active:scale-95 transition-transform"
      style={{ borderColor: theme.subText, backgroundColor: 'rgba(255,255,255,0.08)' }}
    >
      <Binary size={18} color={theme.text} />
      <span className="text-sm font-medium" style={{ color: theme.text }}>
        Verify with Serial number
      </span>
    </button>
  </div>
);

/** Modal for manual Serial Number entry. */
const SerialPopup = ({ theme, serialInput, serialError, onInputChange, onSubmit, onClose }) => (
  <div
    className="fixed inset-0 z-[9999] flex items-center justify-center px-6"
    style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}
  >
    <div className="w-full max-w-md rounded-lg p-6" style={{ backgroundColor: theme.background2 }}>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold" style={{ color: theme.text }}>Enter Serial Number</h2>
        <button onClick={onClose} className="p-1" style={{ color: theme.gray }}>
          <XCircle size={24} />
        </button>
      </div>

      {/* Input */}
      <div className="mb-4">
        <input
          type="text"
          value={serialInput}
          onChange={onInputChange}
          onKeyUp={(e) => { if (e.key === 'Enter') onSubmit(); }}
          placeholder="Enter Serial Number (e.g., F2L9Q3X7N6Y8)"
          className="w-full p-3 rounded-lg border"
          style={{
            backgroundColor: theme.background1,
            color: theme.text,
            borderColor: serialError ? '#EF4444' : theme.stroke,
            borderWidth: 1,
          }}
          autoFocus
        />
        {serialError && (
          <p className="text-sm mt-2" style={{ color: '#EF4444' }}>{serialError}</p>
        )}
      </div>

      {/* Helper text */}
      <div className="mb-4 p-3 rounded-lg" style={{ backgroundColor: theme.background1 }}>
        <p className="text-sm" style={{ color: theme.gray }}>
          Enter the Serial Number found on the product packaging or QR code.
        </p>
      </div>

      {/* Submit */}
      <button
        type="button"
        onClick={onSubmit}
        className="w-full py-3 rounded-lg font-semibold transition-all hover:opacity-90"
        style={{ backgroundColor: theme.button, color: '#ffffff' }}
      >
        Verify Product
      </button>
    </div>
  </div>
);

/** Camera permission-denied modal. */
const PermissionModal = ({ theme, message, onRetry, onDismiss }) => (
  <div
    className="fixed inset-0 z-50 flex items-center justify-center"
    style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}
    onClick={(e) => { if (e.target === e.currentTarget) onDismiss(); }}
  >
    <div
      className="relative mx-6 w-full max-w-sm rounded-2xl p-6 shadow-2xl"
      style={{ backgroundColor: theme.background2 }}
    >
      <button
        onClick={onDismiss}
        className="absolute top-3 right-3 p-1 rounded-full"
        style={{ color: theme.subText }}
        aria-label="Dismiss"
      >
        <X size={18} />
      </button>

      <div className="flex justify-center mb-4">
        <div
          className="w-16 h-16 rounded-full flex items-center justify-center"
          style={{ backgroundColor: 'rgba(239,68,68,0.15)' }}
        >
          <ShieldAlert size={32} style={{ color: '#ef4444' }} />
        </div>
      </div>

      <h2 className="text-lg font-bold text-center mb-2" style={{ color: theme.text }}>
        Camera Access Required
      </h2>
      <p className="text-sm text-center mb-6 leading-relaxed" style={{ color: theme.subText }}>
        {message}
      </p>

      <button
        onClick={onRetry}
        className="w-full py-3 rounded-xl font-semibold text-white text-sm mb-3 transition-all hover:opacity-90 active:scale-[0.98]"
        style={{ backgroundColor: theme.button }}
      >
        Try Again
      </button>
      <button
        onClick={onDismiss}
        className="w-full py-3 rounded-xl font-medium text-sm transition-all hover:opacity-80"
        style={{ color: theme.subText }}
      >
        Not Now
      </button>
    </div>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// Screen
// ─────────────────────────────────────────────────────────────────────────────

const ScanQRScreen = () => {
  const navigate = useNavigate();

  const [isDark] = useTheme();
  const theme = isDark ? darkTheme : lightTheme;

  // Scanner mode: 'display' shows the QR + buttons; 'scan' activates the camera
  const [mode, setMode]                   = useState('display');
  const [permissionError, setPermissionError] = useState(null);
  const [scanError, setScanError]         = useState(null);

  // Serial Number manual-entry popup
  const [showSerialPopup, setShowSerialPopup] = useState(false);
  const [serialInput, setSerialInput]         = useState('');
  const [serialError, setSerialError]         = useState('');

  const qrValue = `${window.location.origin}/onboard`;

  // ── Scanner hook ────────────────────────────────────────────────────────────
  const { triggerScan } = useQrScanner({
    mode,
    setMode,
    onScanned:         handleBarcodeScanned,
    onPermissionError: setPermissionError,
    onScanError:       setScanError,
  });

  // ── Navigation helper: resolve scanned data → route ─────────────────────────
  function handleBarcodeScanned(data) {
    // Extract URL fragment (#suspected, #expired …)
    const hashIdx = data.indexOf('#');
    const hash    = hashIdx !== -1 ? data.substring(hashIdx) : '';

    // 1. Try GS1 AI string or GS1 Digital Link URL
    const gs1Path = parseGS1(data);
    if (gs1Path) {
      navigate(gs1Path + hash, { state: { isDark, scannedUrl: data } });
      return;
    }

    // 2. Fallback: look up by serial/GTIN across all configured products
    const productId = detectProductFromPath(data);
    if (productId) {
      const cfg = PRODUCTS[productId];
      setActiveProduct(productId);
      navigate(`/01/${cfg.gtin}/21/${cfg.serialNumber}${hash}`, { state: { isDark, scannedUrl: data } });
      return;
    }

    // 3. Last resort — unrecognised payload
    console.warn('[ScanQRScreen] Could not resolve product from:', data);
    navigate('/verification' + hash, { state: { isDark, scannedUrl: data } });
  }

  // ── Scan trigger (clears previous errors before opening camera) ─────────────
  const handleScanTrigger = () => {
    setPermissionError(null);
    setScanError(null);
    triggerScan();
  };

  // ── Serial Number popup handlers ─────────────────────────────────────────
  const handleOpenSerialPopup = () => {
    setShowSerialPopup(true);
    setSerialInput('');
    setSerialError('');
  };

  const handleCloseSerialPopup = () => {
    setShowSerialPopup(false);
    setSerialInput('');
    setSerialError('');
  };

  const handleSerialSubmit = () => {
    const serial = serialInput.trim();

    if (!serial) {
      setSerialError('Please enter a Serial Number');
      return;
    }

    // Search for matching serial number in all products and verification states
    let foundMatch = null;
    let verificationHash = '';

    for (const [productId, cfg] of Object.entries(PRODUCTS)) {
      // Check all verification states (verified, suspected, expired)
      for (const [state, qrData] of Object.entries(cfg.qrCodes || {})) {
        if (qrData.serialNumber === serial) {
          foundMatch = { productId, cfg, state };
          verificationHash = qrData.hash || '';
          break;
        }
      }
      if (foundMatch) break;
    }

    if (foundMatch) {
      const { productId, cfg } = foundMatch;
      setActiveProduct(productId);
      handleCloseSerialPopup();
      // Use full GS1 path /01/{gtin}/21/{serial} with verification hash
      navigate(`/01/${cfg.gtin}/21/${serial}/verification/${verificationHash}`, { state: { isDark } });
    } else {
      setSerialError('Invalid Serial Number. Please try again.');
    }
  };

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div
      className="fixed top-0 left-1/2 -translate-x-1/2 w-full h-full overflow-hidden"
      style={{ backgroundColor: theme.background1 }}
    >
      {/* Header */}
      <div className="absolute top-12 left-1/2 -translate-x-1/2 w-full flex justify-between items-center px-6 z-20">
        <button
          className="p-1"
          onClick={() => navigate('/onboard', { state: { isDark } })}
          style={{ color: theme.text }}
          aria-label="Go back"
        >
          <ChevronLeft size={24} />
        </button>

        <h1 className="text-lg font-semibold" style={{ color: theme.text }}>
          Verification
        </h1>

        {mode === 'scan' && (
          <button
            onClick={() => setMode('display')}
            className="w-10 h-10 rounded-full flex items-center justify-center"
            style={{ color: theme.text }}
            aria-label="Cancel scan"
          >
            <X size={20} color="#fff" />
          </button>
        )}
        {mode === 'display' && (
          <div className="w-10 h-10" />
        )}
      </div>

      {/* Scan mode: full-screen camera + overlay */}
      {mode === 'scan' && (
        <div className="absolute inset-0 w-full h-full z-0 bg-black">
          <div id={QR_ELEMENT_ID} className="w-full h-full" />
          <ScanOverlay buttonColor={theme.button} />
        </div>
      )}

      {/* Display mode: device QR + action buttons */}
      {mode === 'display' && (
        <DisplayPanel
          theme={theme}
          qrValue={qrValue}
          scanError={scanError}
          onScanTrigger={handleScanTrigger}
          onOpenSerialPopup={handleOpenSerialPopup}
        />
      )}

      {/* GTIN manual-entry popup */}
      {showSerialPopup && (
        <SerialPopup
          theme={theme}
          serialInput={serialInput}
          serialError={serialError}
          onInputChange={(e) => setSerialInput(e.target.value)}
          onSubmit={handleSerialSubmit}
          onClose={handleCloseSerialPopup}
        />
      )}

      {/* Camera permission error modal */}
      {permissionError && (
        <PermissionModal
          theme={theme}
          message={permissionError}
          onRetry={() => { setPermissionError(null); handleScanTrigger(); }}
          onDismiss={() => setPermissionError(null)}
        />
      )}

      {/* Global styles for html5-qrcode DOM injection */}
      <style>{`
        #${QR_ELEMENT_ID} {
          border: none !important;
          width: 100% !important;
          height: 100% !important;
          padding: 0 !important;
          background: black;
          overflow: hidden;
        }
        #${QR_ELEMENT_ID} > * { border: none !important; }
        #${QR_ELEMENT_ID} video {
          width: 100% !important;
          height: 100% !important;
          object-fit: cover !important;
        }
        /* Keep library canvas functional but hidden from view */
        #${QR_ELEMENT_ID} canvas {
          position: absolute !important;
          top: -9999px !important;
          left: -9999px !important;
          pointer-events: none !important;
        }
        #${QR_ELEMENT_ID} img { display: none !important; }

        @keyframes scanLine {
          0%   { transform: translateY(0px);   opacity: 0; }
          10%  { opacity: 1; }
          90%  { opacity: 1; }
          100% { transform: translateY(248px); opacity: 0; }
        }

        @keyframes pulse-ring {
          0%, 100% {
            transform: scale(1);
            opacity: 0.2;
          }
          50% {
            transform: scale(1.05);
            opacity: 0.1;
          }
        }

        @keyframes bounce-subtle {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-4px);
          }
        }

        @keyframes pulse-dot {
          0% {
            box-shadow: 0 0 0 0 currentColor;
            opacity: 1;
          }
          70% {
            box-shadow: 0 0 0 6px transparent;
            opacity: 0.7;
          }
          100% {
            box-shadow: 0 0 0 0 transparent;
            opacity: 1;
          }
        }

        @keyframes scale-pulse {
          0%, 100% {
            transform: scale(1);
          }
          50% {
            transform: scale(1.08);
          }
        }
      `}</style>
    </div>
  );
};

export default ScanQRScreen;
